// externals
import type { PropsWithChildren } from 'react';
import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
// libraries

import { BootstrapLoadingState } from '@makemydeal/dr-shared-types';
import type { StateTree } from '@makemydeal/dr-dash-types';
import {
    bootstrapSharedActionCreators,
    dealerSelectors,
    featureToggleSelectors,
    sharedBootstrapSelectors
} from '@makemydeal/dr-shared-store';
import {
    locHrefUtil,
    hostUrlHelper,
    HostEnvironment,
    loadingIndicatorUtils,
    MANAGER_VIEW_APP_PREFIX
} from '@makemydeal/dr-shared-ui-utils';
import { LoadingPage, SoftPullConsentModal } from '@makemydeal/dr-dash-components';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as customerSpdSelectors from '@makemydeal/dr-dash-store/selectors/customerSpdSelectors';
import * as customerSpdActionCreators from '@makemydeal/dr-dash-store/actions/CustomerSpdActionCreators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as deskingActionCreators from '@makemydeal/dr-dash-store/actions/deskingActionCreators';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';

export const Bootstrapper: React.FC<PropsWithChildren> = ({ children }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const dealXgId = queryParams.get('dealXgId');
    const dealerId = useSelector(dealerSelectors.getDealerId);
    const loadingState = useSelector(sharedBootstrapSelectors.getBootstrapLoadingState);
    const loadingErrorMessage = useSelector(sharedBootstrapSelectors.getBootstrapErrorMessage);
    const bootstrapPayloadErrorMessage = useSelector(sharedBootstrapSelectors.getBootstrapPayloadErrorMessage);
    const environment = hostUrlHelper.getHostUrlEnvironment(MANAGER_VIEW_APP_PREFIX, locHrefUtil.getLocHref());
    const isBootstrapSuccessful = loadingState === BootstrapLoadingState.LoadedSuccessfully;

    // Soft pull consent feature
    const softPullConsentProvided = useSelector(offerInfoSelectors.getSoftPullConsentProvided);
    const showSoftPullConsentModal = useSelector(offerInfoSelectors.getShowSoftPullConsentModal);
    const isEnableSoftPullConsentMV = useSelector((state: StateTree) =>
        isBootstrapSuccessful ? featureToggleSelectors.isEnableSoftPullConsentMV(state) : false
    );
    const isSpdManagementEnabled = useSelector(dealerSelectors.isSpdManagementEnabled);
    const buyerCommonConsumerId = useSelector(offerReduxSelectors.getShopperCommonConsumerId);
    const hasIdentityDataCompleteness = useSelector((state: StateTree) =>
        isBootstrapSuccessful ? customerSpdSelectors.getHasIdentityDataCompleteness(state) : false
    );
    const isTimeToVisualCompletionEnabled = useSelector((state: StateTree) =>
        isBootstrapSuccessful ? featureToggleSelectors.isTimeToVisualCompletionMVEnabled(state) : false
    );
    const hasRecordedVisualCompletion = useRef(false);
    const checkSpdDataSelector = useMemo(
        () => (buyerCommonConsumerId ? customerSpdSelectors.getCheckCustomerSpdResult(buyerCommonConsumerId) : () => undefined),
        [buyerCommonConsumerId]
    );
    const checkSpdData = useSelector(checkSpdDataSelector);

    const vehicleAnalyticsPayload = useSelector(vehicleSelectors.getVehicleAnalyticsPayload);

    useEffect(() => {
        // load the deal props from QS
        dispatch(bootstrapSharedActionCreators.initialLoad());
        // fetch the deal
        dispatch(bootstrapSharedActionCreators.fetchBootstrapData(MANAGER_VIEW_APP_PREFIX, { loadDraftScenario: true }));
    }, [dispatch]);

    useEffect(() => {
        if (loadingState === BootstrapLoadingState.LoadFailed) {
            logNewRelicPageAction('MV: BootstrapError', {
                dealXgId,
                url: window.location.href,
                ...(dealerId && { dealerId }),
                error: bootstrapPayloadErrorMessage
            });
        }
        if (loadingState !== BootstrapLoadingState.LoadedSuccessfully && loadingState !== BootstrapLoadingState.LoadFailed) {
            return;
        }

        loadingIndicatorUtils.removeAllLoadingIndicatorPanels();

        // Record page load duration only when bootstrap completes successfully
        if (!isTimeToVisualCompletionEnabled && loadingState === BootstrapLoadingState.LoadedSuccessfully) {
            const now = performance.now();
            const loadingIndicatorDisappearsDuration = now;
            const bootstrapSuccessMark = performance.getEntriesByName('bootstrap-success-time', 'mark')[0];
            const bootstrapSuccessToLoaderDisappearsDuration = bootstrapSuccessMark ? now - bootstrapSuccessMark.startTime : NaN;
            logNewRelicPageAction('MV:LoadingIndicatorDisappears', {
                loadingIndicatorDisappearsDuration,
                bootstrapSuccessToLoaderDisappearsDuration
            });
        }

        // Time to Visual Completion — double-rAF measurement (gated by toggle)
        if (
            isTimeToVisualCompletionEnabled &&
            loadingState === BootstrapLoadingState.LoadedSuccessfully &&
            !hasRecordedVisualCompletion.current
        ) {
            hasRecordedVisualCompletion.current = true;
            const reactCommitTime = performance.now(); // ms since performance.timeOrigin (navigation start)

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const paintCompleteTime = performance.now();
                    const loadingIndicatorDisappearsDuration = paintCompleteTime;
                    const bootstrapSuccessMark = performance.getEntriesByName('bootstrap-success-time', 'mark')[0];
                    const bootstrapSuccessToLoaderDisappearsDuration = paintCompleteTime - bootstrapSuccessMark.startTime;

                    logNewRelicPageAction('MV:LoadingIndicatorDisappears', {
                        pageLoadToVisualCompletionMs: paintCompleteTime,
                        pageLoadToReactCommitMs: reactCommitTime,
                        reactCommitToPaintCompleteMs: paintCompleteTime - reactCommitTime,
                        loadingIndicatorDisappearsDuration,
                        bootstrapSuccessToLoaderDisappearsDuration,
                        isTimeToVisualCompletionEnabled,
                        documentVisibilityState: document.visibilityState
                    });
                });
            });
        }
    }, [loadingState, isTimeToVisualCompletionEnabled]);

    // Prefetch customer SPD data once bootstrap succeeds and SPD management is enabled
    useEffect(() => {
        if (
            loadingState === BootstrapLoadingState.LoadedSuccessfully &&
            isSpdManagementEnabled &&
            buyerCommonConsumerId &&
            !checkSpdData
        ) {
            dispatch(customerSpdActionCreators.checkCustomerSpdRSAA());
        }
    }, [loadingState, isSpdManagementEnabled, buyerCommonConsumerId, checkSpdData, dispatch]);

    // Show soft pull consent modal when app loads, toggle is enabled, and consent has not been provided and
    // either customer has a driver's license or (when enabled via toggle) all required identity fields are present
    useEffect(() => {
        if (
            loadingState === BootstrapLoadingState.LoadedSuccessfully &&
            isEnableSoftPullConsentMV &&
            softPullConsentProvided === undefined &&
            hasIdentityDataCompleteness
        ) {
            dispatch(offerActionCreators.setShowSoftPullConsentModal(true));
        }
    }, [loadingState, isEnableSoftPullConsentMV, softPullConsentProvided, hasIdentityDataCompleteness, dispatch]);

    const handleSoftPullConsentCancel = () => {
        dispatch(deskingActionCreators.softpullConsentCancelClicked(vehicleAnalyticsPayload)); // dc-mv-283: Analytics tag
        dispatch(offerActionCreators.setSoftPullConsentProvided(false)); // Explicitly set consent to false on cancel
        dispatch(offerActionCreators.setShowSoftPullConsentModal(false)); // Close the modal
    };

    const handleSoftPullConsentAccept = () => {
        dispatch(deskingActionCreators.softpullConsentContinueClicked(vehicleAnalyticsPayload)); // dc-mv-277: Analytics tag
        dispatch(offerActionCreators.setSoftPullConsentProvided(true)); // Explicitly set consent to true on accept
        dispatch(offerActionCreators.setShowSoftPullConsentModal(false)); // Close the modal
    };

    useEffect(() => {
        window.scroll({
            top: 0,
            left: 0
        });
    }, [location]);

    if (loadingErrorMessage) return <>{loadingErrorMessage}</>;

    return (
        <>
            <SoftPullConsentModal
                show={showSoftPullConsentModal}
                onCancel={handleSoftPullConsentCancel}
                onAccept={handleSoftPullConsentAccept}
            />
            {loadingState === BootstrapLoadingState.Loading && <LoadingPage />}
            {loadingState === BootstrapLoadingState.LoadedSuccessfully && children}
            {loadingState === BootstrapLoadingState.LoadFailed && (
                <>
                    {environment === HostEnvironment.Local ? (
                        <>
                            An error occurred when bootstrapping the app- you will see this when running locally if (1) the BFF
                            hasn't yet started up, (2) it isn't running properly, or (3) you have invalid query params. Try
                            refreshing the browser if the first scenario applies.
                        </>
                    ) : (
                        <>Unable to bootstrap the application - either a network error or service availability issue occurred.</>
                    )}
                </>
            )}
            {loadingState.toString() === '' && 'Error occurred bootstrapping the app - unsupport state.'}
        </>
    );
};
